import type { Dispatch, SetStateAction } from 'react'

import type { Row } from '@tanstack/react-table'

import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { useToast } from '@/components/ui/toast.tsx'
import { useUpdateTeamRoleTable } from '@/modules/team/hooks/use-update-team-role'
import type { TeamRole } from '@/modules/team/types'
import { handleError } from '@/utils/error'

interface TeamRoleRemoveDialogType {
  row: Row<TeamRole>
  showRemoveDialog: boolean
  setRemoveEditDialog: Dispatch<SetStateAction<boolean>>
}

export const TeamRoleRemoveDialog = ({
  row,
  showRemoveDialog,
  setRemoveEditDialog,
}: TeamRoleRemoveDialogType) => {
  const { deleteRole } = useUpdateTeamRoleTable()

  const toast = useToast()

  const handleCloseDialog = () => {
    setRemoveEditDialog(false)
  }

  const handleSubmit = async () => {
    await deleteRole
      .mutateAsync(row.original.id)
      .then(() => {
        toast.success('Successfully deleted role')
      })
      .catch((error) => {
        handleError(
          error,
          'Something went wrong while deleting the role. Try again later.',
        )
      })

    handleCloseDialog()
  }

  return (
    <Dialog
      show={showRemoveDialog}
      onClose={handleCloseDialog}
    >
      <Dialog.Header>
        <Dialog.Title>Are you sure?</Dialog.Title>
        <Dialog.Description className="text-center">
          This action cannot be undone and will permanently remove the following
          role: {row.original.name}
        </Dialog.Description>
      </Dialog.Header>

      <Dialog.ClosableFooter
        onClose={handleCloseDialog}
        className="mt-6"
      >
        <Button
          variant="destructive"
          onClick={handleSubmit}
          isLoading={deleteRole.isLoading}
        >
          Delete
        </Button>
      </Dialog.ClosableFooter>
    </Dialog>
  )
}
